import { inspectedFetch } from '../../core/index.js'
import { ApiError } from '../../core/index.js'
import type { ApiResponse } from '../../core/index.js'

import type { Facility, FacilityAddress } from '../../facilities/index.js'

import type { PerformanceSummary } from '../../../features/performance_reviews/types.js'

/**
 * The part of a demo that only an operator can do. The admin credential
 * sets a facility up, but its address is the operator's alone to record, so
 * the demo signs in with the operator login it just created - the same
 * `GET /auth/login` the dashboard uses - and sends `PATCH /facilities/{id}`
 * with that token. The token lives only in this function's caller.
 */

export interface OperatorSession {
  accessToken: string
}

export async function loginAsOperator(apiBaseUrl: string, email: string, password: string): Promise<OperatorSession> {
  const response = await inspectedFetch(`${apiBaseUrl}/auth/login`, { headers: { Authorization: `Basic ${btoa(`${email}:${password}`)}` } })
  let body: ApiResponse<{ accessToken: string }> | null = null
  try {
    body = (await response.json()) as ApiResponse<{ accessToken: string }>
  } catch {
    // Non-JSON body - falls through to the failure below.
  }
  if (!response.ok || !body?.success || !body.data?.accessToken) {
    throw new ApiError(body?.error ?? `The operator login for ${email} failed (${response.status})`, response.status)
  }
  return { accessToken: body.data.accessToken }
}

export async function setFacilityAddress(apiBaseUrl: string, session: OperatorSession, facilityId: string, address: FacilityAddress, phone?: string): Promise<Facility> {
  const path = `/facilities/${encodeURIComponent(facilityId)}`
  const response = await inspectedFetch(`${apiBaseUrl}${path}`, {
    method: 'PATCH',
    headers: { Authorization: `Bearer ${session.accessToken}`, 'content-type': 'application/json' },
    body: JSON.stringify({ address, ...(phone ? { phone } : {}) }),
  })
  let body: ApiResponse<Facility> | null = null
  try {
    body = (await response.json()) as ApiResponse<Facility>
  } catch {
    // Non-JSON body - falls through to the failure below.
  }
  if (!response.ok || !body?.success || body.data === undefined) {
    throw new ApiError(body?.error ?? `Request to ${path} failed (${response.status})`, response.status)
  }
  return body.data
}

async function operatorCall<T>(apiBaseUrl: string, session: OperatorSession, method: 'GET' | 'POST', path: string, payload?: unknown): Promise<T> {
  const response = await inspectedFetch(`${apiBaseUrl}${path}`, {
    method,
    headers: { Authorization: `Bearer ${session.accessToken}`, ...(payload === undefined ? {} : { 'content-type': 'application/json' }) },
    body: payload === undefined ? undefined : JSON.stringify(payload),
  })
  let body: ApiResponse<T> | null = null
  try {
    body = (await response.json()) as ApiResponse<T>
  } catch {
    // Non-JSON body - falls through to the failure below.
  }
  if (!response.ok || !body?.success || body.data === undefined) {
    throw new ApiError(body?.error ?? `Request to ${path} failed (${response.status})`, response.status)
  }
  return body.data
}

/** Generates a worker's performance summary for the year to date, as the operator, so the demo has one to open. */
export function generateSummaryAsOperator(apiBaseUrl: string, session: OperatorSession, workerId: string): Promise<PerformanceSummary> {
  return operatorCall(apiBaseUrl, session, 'POST', `/workers/${encodeURIComponent(workerId)}/performance-summaries`, {})
}

/** Exports a facility's summaries for HR as the operator - the records, not the file - so the facility has a receipt. Returns how many workers it covered. */
export async function exportSummariesAsOperator(apiBaseUrl: string, session: OperatorSession, facilityId: string): Promise<number> {
  const summaries = await operatorCall<PerformanceSummary[]>(apiBaseUrl, session, 'GET', `/facilities/${encodeURIComponent(facilityId)}/performance-summaries/export?format=json`)
  return summaries.length
}

/** Releases a summary to the worker's own app, as the operator, so the demo's worker seat shows it. */
export function releaseSummaryAsOperator(apiBaseUrl: string, session: OperatorSession, workerId: string, summaryId: string): Promise<PerformanceSummary> {
  return operatorCall(apiBaseUrl, session, 'POST', `/workers/${encodeURIComponent(workerId)}/performance-summaries/${encodeURIComponent(summaryId)}/release`, {})
}
